import React, { useState, useEffect } from 'react';
import { previousCloseService } from '../services/previousCloseService';
import { showToast } from '../utils/toast';
import './PreviousClose.css';

const PreviousClose = () => {
    // API Key state
    const [apiKey, setApiKey] = useState("");
    const [apiKeyValid, setApiKeyValid] = useState(false);
    const [userInfo, setUserInfo] = useState(null);
    const [initializing, setInitializing] = useState(true);

    // Data state
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [availableSymbols, setAvailableSymbols] = useState([]);
    const [selectedSymbol, setSelectedSymbol] = useState('');
    const [currentSymbolData, setCurrentSymbolData] = useState(null);
    const [responseTime, setResponseTime] = useState(null);
    const [dataAge, setDataAge] = useState(null);
    const [dataSource, setDataSource] = useState(null);

    // Load API key from localStorage on component mount
    useEffect(() => {
        const initializeApiKey = async () => {
            const savedApiKey = localStorage.getItem("previous_close_api_key");
            if (savedApiKey) {
                setApiKey(savedApiKey);
                await validateApiKey(savedApiKey);
            }
            setInitializing(false);
        };

        initializeApiKey();
    }, []);

    // Validate API key by fetching available symbols
    const validateApiKey = async (key) => {
        if (!key || key.trim() === "") {
            setApiKeyValid(false);
            setError("Please enter your API key");
            return false;
        }

        try {
            setLoading(true);
            setError("");

            // Test API key with available symbols request
            const response = await previousCloseService.getAvailableSymbols(key);

            if (response.success) {
                setApiKeyValid(true);
                setAvailableSymbols(response.symbols || []);
                localStorage.setItem("previous_close_api_key", key);
                showToast.success("API key validated successfully!");
                return true;
            } else {
                setApiKeyValid(false);
                setError(response.error || "Invalid API key");
                localStorage.removeItem("previous_close_api_key");
                showToast.error("Invalid API key");
                return false;
            }
        } catch (error) {
            console.error("API key validation error:", error);
            setApiKeyValid(false);
            setError("Failed to validate API key");
            localStorage.removeItem("previous_close_api_key");
            showToast.error("Failed to validate API key");
            return false;
        } finally {
            setLoading(false);
        }
    };

    // Handle API key submission
    const handleApiKeySubmit = async (e) => {
        e.preventDefault();
        await validateApiKey(apiKey);
    };

    // Clear API key
    const clearApiKey = () => {
        setApiKey("");
        setApiKeyValid(false);
        setUserInfo(null);
        setAvailableSymbols([]);
        setCurrentSymbolData(null);
        setSelectedSymbol("");
        setError("");
        setLoading(false);
        localStorage.removeItem("previous_close_api_key");
        showToast.info("API key cleared");
    };

    // Fetch previous close data for selected symbol
    const fetchPreviousCloseData = async (symbol) => {
        if (!apiKeyValid || !apiKey || !symbol) {
            setError("Please provide a valid API key and select a symbol");
            return;
        }

        try {
            setLoading(true);
            setError("");

            const startTime = Date.now();
            const response = await previousCloseService.getPreviousCloseData(symbol, apiKey);

            if (response.success) {
                setCurrentSymbolData(response.data);
                setResponseTime(Date.now() - startTime);
                setDataAge(response.data_age_seconds);
                setDataSource(response.source);
                setUserInfo({ email: response.user });
                showToast.success(`Data for ${symbol} fetched successfully!`);
            } else {
                setError(response.error || "Failed to fetch previous close data");
                setCurrentSymbolData(null);
                
                // Handle authentication errors
                if (response.error?.includes("Authentication") || response.error?.includes("API key")) {
                    setApiKeyValid(false);
                    localStorage.removeItem("previous_close_api_key");
                }
            }
        } catch (error) {
            console.error("Error fetching previous close data:", error);
            setError("Network error. Please try again.");
            setCurrentSymbolData(null);
        } finally {
            setLoading(false);
        }
    };

    // Handle symbol change
    const handleSymbolChange = (e) => {
        const newSymbol = e.target.value;
        setSelectedSymbol(newSymbol);
        if (newSymbol) {
            fetchPreviousCloseData(newSymbol);
        }
    };

    // Refresh data
    const handleRefresh = () => {
        if (selectedSymbol) {
            fetchPreviousCloseData(selectedSymbol);
        }
    };

    // Format data for display
    const formatData = (data) => {
        if (!data) return null;
        
        return {
            close: data.close || 'N/A',
            high: data.high || 'N/A',
            low: data.low || 'N/A',
            open: data.open || 'N/A',
            volume: data.volume || 'N/A',
            timestamp: data.timestamp || 'N/A',
            date: data.date || 'N/A',
            symbol: data.symbol || selectedSymbol
        };
    };

    // Helper function to format timestamp
    const formatTimestamp = (timestamp) => {
        if (!timestamp || timestamp === 'N/A') return 'N/A';
        
        try {
            const date = typeof timestamp === 'number' 
                ? new Date(timestamp)
                : new Date(timestamp);
            
            if (isNaN(date.getTime())) return 'Invalid Date';
            
            return date.toLocaleString();
        } catch (error) {
            return 'Invalid Date';
        }
    };

    const formattedData = formatData(currentSymbolData);

    // Show loading during initialization
    if (initializing) {
        return (
            <div className="container py-4">
                <div className="card shadow">
                    <div className="card-body text-center py-5">
                        <div className="spinner-border text-primary mb-3" role="status">
                            <span className="visually-hidden">Loading...</span>
                        </div>
                        <h5>Initializing...</h5>
                        <p className="text-muted">Checking for saved API key...</p>
                    </div>
                </div>
            </div>
        );
    }

    // Show API key form if not authenticated
    if (!apiKeyValid) {
        return (
            <div className="container py-4">
                <div className="row justify-content-center">
                    <div className="col-md-6">
                        <div className="card shadow">
                            <div className="card-header bg-primary text-white text-center">
                                <h4 className="mb-0">
                                    <i className="bi bi-key me-2"></i>
                                    API Authentication Required
                                </h4>
                            </div>
                            <div className="card-body">
                                <p className="text-muted text-center mb-4">
                                    Please enter your API key to access previous close data.
                                </p>

                                <form onSubmit={handleApiKeySubmit}>
                                    <div className="mb-3">
                                        <label htmlFor="apiKey" className="form-label fw-bold">
                                            API Key
                                        </label>
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="apiKey"
                                            placeholder="Enter your API key"
                                            value={apiKey}
                                            onChange={(e) => setApiKey(e.target.value)}
                                            required
                                            disabled={loading}
                                        />
                                        <div className="form-text">
                                            Your API key is required to authenticate and track your usage.
                                        </div>
                                    </div>

                                    {error && (
                                        <div className="alert alert-danger">
                                            <i className="bi bi-exclamation-triangle me-2"></i>
                                            {error}
                                        </div>
                                    )}

                                    <button
                                        type="submit"
                                        className="btn btn-primary w-100"
                                        disabled={loading || !apiKey.trim()}
                                    >
                                        {loading ? (
                                            <>
                                                <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                                Validating...
                                            </>
                                        ) : (
                                            <>
                                                <i className="bi bi-shield-check me-2"></i>
                                                Validate & Continue
                                            </>
                                        )}
                                    </button>
                                </form>

                                <div className="mt-4 p-3 bg-light rounded">
                                    <h6 className="fw-bold mb-2">
                                        <i className="bi bi-info-circle me-2"></i>
                                        How to get your API Key:
                                    </h6>
                                    <ol className="mb-0 small">
                                        <li>Go to your account dashboard</li>
                                        <li>Navigate to API Settings</li>
                                        <li>Copy your API key</li>
                                        <li>Paste it in the field above</li>
                                    </ol>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container py-4">
            <div className="card shadow">
                <div className="card-header bg-primary text-white">
                    <div className="d-flex justify-content-between align-items-center">
                        <div>
                            <h3 className="mb-1">Previous Close Data</h3>
                            <small>
                                Historical forex data |
                                {userInfo && ` User: ${userInfo.email}`}
                            </small>
                        </div>
                        <button
                            className="btn btn-outline-light btn-sm"
                            onClick={clearApiKey}
                            title="Change API Key"
                        >
                            <i className="bi bi-key"></i>
                        </button>
                    </div>
                </div>

                <div className="card-body">
                    {/* Controls */}
                    <div className="row mb-4">
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Select Symbol:</label>
                            <select 
                                value={selectedSymbol} 
                                onChange={handleSymbolChange}
                                className="form-select"
                                disabled={loading}
                            >
                                <option value="">Select Symbol</option>
                                {availableSymbols.map(symbol => (
                                    <option key={symbol} value={symbol}>
                                        {symbol}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label fw-bold">Actions:</label>
                            <div className="d-flex gap-2">
                                <button 
                                    onClick={handleRefresh} 
                                    disabled={loading || !selectedSymbol}
                                    className="btn btn-success"
                                >
                                    {loading ? (
                                        <>
                                            <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                                            Loading...
                                        </>
                                    ) : (
                                        <>
                                            <i className="bi bi-arrow-clockwise me-2"></i>
                                            Refresh
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Error Display */}
                    {error && (
                        <div className="alert alert-danger">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            {error}
                        </div>
                    )}

                    {/* Data Display */}
                    {!loading && !error && formattedData && (
                        <div className="card">
                            <div className="card-header bg-light">
                                <div className="d-flex justify-content-between align-items-center">
                                    <h5 className="mb-0">{formattedData.symbol || selectedSymbol} - Previous Close</h5>
                                    <div className="d-flex gap-3 small text-muted">
                                        {responseTime && <span>Response: {responseTime}ms</span>}
                                        {dataAge && <span>Data Age: {dataAge}s</span>}
                                        {dataSource && <span>Source: {dataSource}</span>}
                                    </div>
                                </div>
                            </div>
                            <div className="card-body">
                                <div className="row">
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Close Price</label>
                                        <div className="fs-4 text-primary">{formattedData.close}</div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Open Price</label>
                                        <div className="fs-5">{formattedData.open}</div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">High Price</label>
                                        <div className="fs-5 text-success">{formattedData.high}</div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Low Price</label>
                                        <div className="fs-5 text-danger">{formattedData.low}</div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Volume</label>
                                        <div>{formattedData.volume}</div>
                                    </div>
                                    <div className="col-md-6 mb-3">
                                        <label className="form-label fw-bold">Date/Time</label>
                                        <div>{formatTimestamp(formattedData.timestamp)}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* No Data State */}
                    {!loading && !error && !formattedData && selectedSymbol && (
                        <div className="text-center py-5">
                            <i className="bi bi-info-circle" style={{ fontSize: '3rem', color: '#6c757d' }}></i>
                            <h5 className="mt-3 text-muted">No Data Available</h5>
                            <p className="text-muted">
                                No previous close data available for {selectedSymbol}. Please try again later.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PreviousClose;